import { createContext, useCallback, useContext, useRef, useState } from "react";

import Modal from "../components/ui/Modal";

export type ConfirmDetails = Omit<ModalDetails, "onClick"> & {
  afterClose?: (accepted: boolean) => Promise<void> | void;
  onConfirm?: () => boolean | Promise<boolean | void> | void;
};

export type ModalDetails = Omit<
  React.ComponentProps<typeof Modal>,
  "children" | "loading" | "onClick" | "onExited" | "open"
> & {
  content: React.ReactNode;
  onClick?: ModalOnClick;
  replace?: boolean;
};

export type ModalId = number;

type ModalOnClick = (accept: boolean) => boolean | Promise<boolean | void> | void;

type ModalState = {
  details: ModalDetails | undefined;
  disappearing: boolean;
  loading: "cancel" | "confirm" | undefined;
  openId: ModalId | null;
};

type ProviderProps = {
  children: JSX.Element;
};

const closedState: ModalState = { details: undefined, disappearing: false, loading: undefined, openId: null };

const ModalContext = createContext({
  addModal: (_modal: ModalDetails): ModalId | null => null,
  removeModal: (_id: ModalId | null) => {},
});

export function ModalProvider({ children }: ProviderProps): JSX.Element {
  const [state, setState] = useState<ModalState>(closedState);
  const stateRef = useRef(state);
  const nextModalId = useRef<ModalId>(0);
  const addModalWithinClick = useRef(false);

  const patchState = useCallback((patch: Partial<ModalState>) => {
    stateRef.current = { ...stateRef.current, ...patch };
    setState(stateRef.current);
  }, []);

  const closeModal = useCallback(() => {
    patchState({ disappearing: true, openId: null });
  }, [patchState]);

  const onModalExited = useCallback(() => {
    if (!stateRef.current.disappearing) return;

    patchState({ details: undefined, disappearing: false });
  }, [patchState]);

  async function onModalClick(option: boolean) {
    patchState({ loading: option ? "confirm" : "cancel" });
    const modalId = stateRef.current.openId;
    addModalWithinClick.current = true;
    try {
      if ((await stateRef.current.details?.onClick?.(option)) === false) return;
    } finally {
      addModalWithinClick.current = false;
      patchState({ loading: undefined });
    }

    if (modalId !== null && modalId === stateRef.current.openId) closeModal();
  }

  const addModal = useCallback(
    (modal: ModalDetails): ModalId | null => {
      if (stateRef.current.openId !== null && !modal.replace) return null;

      nextModalId.current++;
      if (!addModalWithinClick.current && !stateRef.current.disappearing)
        void stateRef.current.details?.onClick?.(false);

      patchState({ details: modal, disappearing: false, loading: undefined, openId: nextModalId.current });
      return nextModalId.current;
    },
    [patchState],
  );

  const removeModal = useCallback(
    (id: ModalId | null) => {
      if (id === null || id !== stateRef.current.openId) return;

      closeModal();
    },
    [closeModal],
  );

  return (
    <ModalContext.Provider value={{ addModal, removeModal }}>
      {state.details && (
        <Modal
          {...state.details}
          loading={state.loading}
          onClick={onModalClick}
          onExited={onModalExited}
          open={!state.disappearing}
        >
          {state.details.content}
        </Modal>
      )}
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const { addModal, removeModal } = useContext(ModalContext);

  const openModal = useCallback(
    (modal: ModalDetails): (() => void) => {
      const id = addModal(modal);
      return () => {
        removeModal(id);
      };
    },
    [addModal, removeModal],
  );

  const confirm = useCallback(
    async (details: ConfirmDetails): Promise<boolean> => {
      return new Promise((resolve) => {
        const { afterClose, onConfirm, ...modalDetails } = details;
        const id = addModal({
          ...modalDetails,
          onClick: async (accept) => {
            if (accept && onConfirm) {
              const succeeded = await onConfirm();
              if (succeeded === false) return false;
            }
            removeModal(id);
            void afterClose?.(accept);
            resolve(accept);
          },
        });
        if (id === null) resolve(false);
      });
    },
    [addModal, removeModal],
  );

  return { confirm, openModal };
}
